import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { MoreHorizontal } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../../components/ui/dropdown-menu'
import { DecorativeWave } from '../../../../components/ui/DecorativeWave'
import { WindowModeToggle } from '../../../../components/common/WindowModeToggle'
import { useIsActiveTab } from '../../../../pages/TabStack/ActiveTabContext'
import { useSelectionHullRow } from '../SelectionHull'
import type { SectionHeaderProps } from './SectionHeader.types'
import './SectionHeader.scss'
import { getScrollContainer } from '@/src/renderer/lib/dom/getScrollContainer'
import { SELECTION_HULL_DURATION } from '@/src/renderer/lib/motion/selectionHullMotion'

/** So lange wartet ein Klick auf den Titel, ob ein zweiter folgt (Doppelklick = umbenennen). */
export const TITLE_CLICK_DELAY = 250

/** So lange fährt die Fläche nach einem Auswahlwechsel (lang: Klick) und blendet aus. */
const HULL_MOVE_MS = SELECTION_HULL_DURATION * 1000 + 80

/**
 * Wo der Kopf im Fluss steht — auch angeheftet. `getBoundingClientRect` und `offsetTop` geben dann die
 * angeheftete Lage (gemessen in Chromium, 09.10.2026). Die Fläche der Hülle gehört an den Platz im Fluss:
 * Angeheftet liegt sie verdeckt über bzw. unter ihm, und rollt man zurück, landet der Kopf genau auf ihr.
 */
function inFlowRect(el: HTMLElement): DOMRect {
  const { position } = el.style
  el.style.position = 'static'
  const rect = el.getBoundingClientRect()
  el.style.position = position
  return rect
}

/**
 * SectionHeader — Visual group divider in the project task list.
 *
 * Displays a section label with collapse toggle, options menu,
 * and a decorative wave SVG. Supports inline rename via double-click.
 *
 * Interaction states (CSS-driven):
 * - Rest: only title + count + wave visible (chevron hidden)
 * - Hover: chevron and window-mode toggle fade in while the pointer is on the header
 *   or — in a project — on one of its tasks (CSS; no proximity since 03.10.2026)
 * - Collapsed / focused: chevron stays visible
 * - Section hover: options (ellipsis) slides in
 * - Title: click collapses (after TITLE_CLICK_DELAY), double-click renames
 * - Arrow hover: arrow gets --space-700 bg
 *
 * @example
 * <SectionHeader
 *   id="abc"
 *   title="Backlog"
 *   taskCount={5}
 *   collapsed={false}
 *   onToggleCollapse={(id) => {}}
 *   onRename={(id, title) => {}}
 *   onDelete={(id) => {}}
 *   onArchive={(id) => {}}
 * />
 *
 * // Archiviert (#193): im Geschafft-Schalter, Menü „Nur Abschnitt wiederherstellen“,
 * // „Mit 3 Aufgaben wiederherstellen“ und „Löschen“
 * <SectionHeader id="abc" title="Umzug" taskCount={0} collapsed={false} archived reopenCount={3}
 *   onToggleCollapse={() => {}} onRename={() => {}} onDelete={(id) => {}} onUnarchive={(id, withTasks) => {}} />
 */
export const SectionHeader: React.FC<SectionHeaderProps> = ({
  id,
  title,
  taskCount,
  collapsed,
  focused = false,
  selected = false,
  editing = false,
  autoEdit = false,
  dragListeners,
  dragAttributes,
  dragRef,
  onToggleCollapse,
  onRename,
  onDelete,
  onArchive,
  onUnarchive,
  reopenCount = 0,
  onSelect,
  onSelectAllTasks,
  onEditEnd,
  onNamingChange,
  onNavigateDown,
  onNavigateUp,
  readonly = false,
  dragOverlay = false,
  showWindowModeToggle = true,
  archived = false,
  dropTarget,
}) => {
  const { t } = useTranslation()
  const [isEditing, setIsEditing] = useState(autoEdit)
  const [editValue, setEditValue] = useState(autoEdit ? '' : title)
  const [menuOpen, setMenuOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const isSubmittingRef = useRef(false)
  const cancelledRef = useRef(false)
  // „Umbenennen“ im Menü: Das Bearbeiten startet erst, wenn das Menü den Fokus abgibt (#236).
  const renameOnCloseRef = useRef(false)
  // Klick auf den Titel klappt erst nach TITLE_CLICK_DELAY ein — ein Doppelklick benennt um.
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current) }, [])
  const editValueRef = useRef(editValue)
  editValueRef.current = editValue

  // Gated on the active tab so inactive (display:none) project tabs register no observer.
  const isActive = useIsActiveTab()
  const rootRef = useRef<HTMLDivElement>(null)

  // Merge our root ref with dnd-kit's setNodeRef (the root's only other
  // ref). Readonly/virtual and archived sections aren't draggable, so skip dragRef there.
  const draggable = !readonly && !archived
  const setRootRef = useCallback(
    (node: HTMLDivElement | null) => {
      rootRef.current = node
      if (draggable) dragRef?.(node)
    },
    [draggable, dragRef],
  )

  // ── Stuck detection for the sticky header ──
  // The header uses `top: -1px` (SCSS): while PINNED its top edge sits 1px
  // outside the scrollport, so an IntersectionObserver with threshold 1 sees
  // intersectionRatio < 1. The top-edge check filters the other partial-
  // visibility case (header entering from the bottom). Drives the --stuck
  // class → soft fade below the pinned header (no hard edge over the tasks
  // scrolling through beneath).
  const [stuck, setStuck] = useState(false)
  useEffect(() => {
    const el = rootRef.current
    if (!el || !isActive || dragOverlay) {
      setStuck(false)
      return
    }
    const root = getScrollContainer(el)

    // Der Kopf heftet nicht an der Oberkante des Scrollers an, sondern
    // unterhalb des 40px-Verlaufsbandes (SectionHeader.scss, `top: 39px`).
    // Ohne Ausgleich überschritte er die Kante nie und `intersectionRatio`
    // bliebe 1 — der Kopf gälte nie als angeheftet.
    //
    // `top` wird ausgelesen statt angenommen, die Beobachtergrenze wandert per
    // `rootMargin` genau dorthin. Die −1px aus dem SCSS heben sich dabei auf.
    const stickyTop = parseFloat(getComputedStyle(el).top)
    const offset = Number.isFinite(stickyTop) ? stickyTop + 1 : 0

    const observer = new IntersectionObserver(
      ([entry]) => {
        setStuck(
          entry.intersectionRatio < 1 &&
            entry.boundingClientRect.top <= (entry.rootBounds?.top ?? 0),
        )
      },
      { root, rootMargin: `${-offset}px 0px 0px 0px`, threshold: [1] },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [isActive, dragOverlay])

  /*
    **In der Auswahl-Hülle der Seite** (#293, Stufe 4; Prototyp `tools/section-hull-prototype`, Sven
    09.10.2026): Dieselbe Fläche wie bei den Aufgaben fährt auf den Kopf, als Pille. An seinem Platz malt
    sie, der Kopf ist durchsichtig — auch noch, solange sie nach dem Abwählen wegfährt. Angeheftet deckt
    er (sonst schienen die Aufgaben durch, die unter ihm durchrollen): Die Fläche fährt unter ihn, und er
    färbt sich im selben Takt in ihren Farben.
  */
  const marked = focused || selected
  const hull = useSelectionHullRow({ id, selected: marked, elRef: rootRef, measure: inFlowRect, disabled: archived || dragOverlay })
  const inHull = hull === 'painting'
  // Ruht die Seite (Abschnitt ziehen), malt er sich selbst — in der Form der Fläche, sonst wechselte er beim
  // Zug und nach der Landung zwischen Pille und Rechteck.
  const hullResting = hull === 'resting' && marked
  const [hullMarked, setHullMarked] = useState(marked)
  const [hullMoving, setHullMoving] = useState(false)
  if (hullMarked !== marked) {
    setHullMarked(marked)
    setHullMoving(true)
  }
  useEffect(() => {
    if (!hullMoving) return
    const timer = setTimeout(() => setHullMoving(false), HULL_MOVE_MS)
    return () => clearTimeout(timer)
  }, [hullMoving, hullMarked])
  const hullGlass = inHull && !stuck && (marked || hullMoving)
  const hullPinned = inHull && stuck && marked

  // Wird ein angehefteter Kopf vom nächsten Abschnitt nach oben hinausgedrückt,
  // riss er an der Scrollport-Kante hart ab. Der Verlauf der Listenspalte kann
  // das nicht übernehmen: Der Kopf trägt `z-index: 10` und liegt darüber — und
  // hebt man den Verlauf darüber, verblasst der ANGEHEFTETE Kopf mit, der
  // lesbar bleiben soll.
  //
  // Also blendet er sich selbst aus. `--push-out` sagt, wie viele Pixel er
  // bereits über der Kante steht; die Maske im SCSS lässt ihn ab dort weich
  // beginnen. Angeheftet steht er bei 1px (dem Versatz aus `top: -1px`), die
  // Maske greift erst darüber.
  //
  // Die beiden Werte gehen als CSS-Variablen DIREKT aufs Element, nicht durch
  // React-State: Sie ändern sich bei jedem Frame des Scrollens, und es gibt
  // einen Kopf pro Abschnitt — als State rendert bei zehn Abschnitten jeder
  // von ihnen zehnmal pro Sekunde neu, für einen Wert, den nur CSS liest.
  useEffect(() => {
    const el = rootRef.current
    if (!el) return
    if (!isActive || dragOverlay) {
      el.style.removeProperty('--push-out')
      el.style.removeProperty('--push-fade')
      return
    }
    const scroller = getScrollContainer(el)
    if (!scroller) return

    let frame = 0
    const measure = () => {
      const overlap = scroller.getBoundingClientRect().top - el.getBoundingClientRect().top
      const pushOut = Math.max(0, Math.round(overlap))
      // Länge der Weichzeichnung wächst mit: bei 0 ist sie 0, die Maske also
      // wirkungslos — deshalb braucht es keine Umschalt-Klasse, die ein
      // React-Rendern nötig machen würde. Der bloß angeheftete Kopf (1px
      // Versatz) bleibt damit fast scharf, ein hinausgedrückter blendet aus.
      el.style.setProperty('--push-out', `${pushOut}px`)
      el.style.setProperty('--push-fade', `${Math.min(pushOut * 6, 12)}px`)
    }
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        measure()
      })
    }

    // Rollen allein reicht nicht (#273): Rückt der Kopf OHNE Rollen zurück an
    // die Kante — eine Aufgabe klappt auf, Zeilen brechen um, über dem Abschnitt
    // wächst etwas —, blieb der alte Wert stehen. Der angeheftete Kopf war dann
    // oben durchsichtig, und die Aufgaben dahinter schienen über seinem Titel
    // durch. Der Beobachter meldet jede Lageänderung, egal woher; die feinen
    // Stufen sorgen dafür, dass er es auch bei kleinen tut.
    //
    // Der Scroll-Listener bleibt trotzdem: Er misst im selben Bild wie das
    // Rollen, der Beobachter erst im nächsten — beim Hinausdrücken hinkte die
    // Weichzeichnung sonst nach.
    const observer = new IntersectionObserver(() => measure(), {
      root: scroller,
      threshold: Array.from({ length: 21 }, (_, i) => i / 20),
    })

    measure()
    scroller.addEventListener('scroll', onScroll, { passive: true })
    observer.observe(el)
    return () => {
      scroller.removeEventListener('scroll', onScroll)
      observer.disconnect()
      if (frame) cancelAnimationFrame(frame)
    }
  }, [isActive, dragOverlay])

  // Sync title prop into edit value (only when prop changes, not on edit toggle)
  useEffect(() => {
    setEditValue(title)
  }, [title])

  // Enter/exit edit mode driven by parent's editing prop
  useEffect(() => {
    if (editing && !isEditing && !archived) {
      setIsEditing(true)
    } else if (!editing && !autoEdit && isEditing) {
      setIsEditing(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing])

  /*
    `autoEdit` kann NACH dem ersten Bild kommen: `createSection` schreibt den
    Abschnitt und danach noch den Sync-Eintrag, und dazwischen meldet die
    Live-Abfrage ihn schon. `useState(autoEdit)` sah dann nur `false`, und der
    frische Abschnitt blieb ohne Titel und ohne Eingabefeld stehen (01.10.2026).
  */
  useEffect(() => {
    if (autoEdit && !isEditing && !archived) {
      setEditValue('')
      setIsEditing(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEdit])

  /*
    Ein frischer Abschnitt (noch ohne Titel) bekommt seinen Namen. Gemeldet per
    Aufräumer, damit auch das Verschwinden zählt: Escape löscht den Abschnitt,
    und der Kopf geht einfach weg. Die Listen-Werkzeug-Pille taucht so lange ab.
  */
  const onNamingChangeRef = useRef(onNamingChange)
  onNamingChangeRef.current = onNamingChange
  const naming = isEditing && !title && !archived
  useEffect(() => {
    if (!naming) return
    const report = onNamingChangeRef.current
    report?.(id, true)
    return () => report?.(id, false)
  }, [naming, id])

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing) {
      const input = inputRef.current
      if (input) {
        input.focus()
        // Place cursor at end instead of selecting all text
        const len = input.value.length
        input.setSelectionRange(len, len)
      }
    }
  }, [isEditing])

  const handleSubmit = useCallback(() => {
    if (isSubmittingRef.current || cancelledRef.current) return
    isSubmittingRef.current = true
    // Read from ref to guarantee latest value (avoids stale closure)
    const trimmed = editValueRef.current.trim()
    if (trimmed && trimmed !== title) {
      onRename(id, trimmed)
    } else if (!trimmed && !title) {
      // New section with no name entered — delete it (same as Escape)
      onDelete(id)
    } else if (!trimmed) {
      setEditValue(title)
    }
    setIsEditing(false)
    onEditEnd?.()
    requestAnimationFrame(() => {
      isSubmittingRef.current = false
      // Refocus the navigation wrapper so keyboard events continue working
      const sectionEl = document.querySelector(`[data-section-id="${id}"]`)
      const wrapper = sectionEl?.closest('[tabindex]') as HTMLElement
      wrapper?.focus({ preventScroll: true })
    })
  }, [title, id, onRename, onDelete, onEditEnd])

  const handleInputKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      // New section with no saved title → cancel creation (delete)
      if (!title) {
        cancelledRef.current = true
        onDelete(id)
        return
      }
      setEditValue(title)
      setIsEditing(false)
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      handleSubmit()
      onNavigateDown?.()
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      handleSubmit()
      onNavigateUp?.()
    }
  }, [handleSubmit, title, id, onDelete, onNavigateDown, onNavigateUp])

  return (
    <div
      ref={setRootRef}
      className={`section-header${marked ? ' section-header--focused' : ''}${readonly ? ' section-header--readonly' : ''}${collapsed && !archived ? ' section-header--collapsed' : ''}${stuck ? ' section-header--stuck' : ''}${archived ? ' section-header--archived' : ''}${inHull ? ' section-header--hull' : ''}${hullGlass ? ' section-header--hull-glass' : ''}${hullPinned ? ' section-header--hull-pinned' : ''}${inHull && hullMoving ? ' section-header--hull-moving' : ''}${hullResting ? ' section-header--hull-resting' : ''}${dropTarget === 'end' ? ' section-header--drop-end' : ''}`}
      data-section-id={id}
      {...(draggable && !isEditing ? dragAttributes : undefined)}
      {...(draggable && !isEditing ? dragListeners : undefined)}
      onClick={(e) => {
        // Clicks on the wave area or empty space (not label/arrow/options/toggle) → select
        const target = e.target as HTMLElement
        if (target.closest('.section-header__label') || target.closest('.section-header__arrow') || target.closest('.section-header__options') || target.closest('.section-header__window-mode')) return
        onSelect?.(id)
      }}
      onDoubleClick={(e) => {
        const target = e.target as HTMLElement
        if (target.closest('.section-header__arrow') || target.closest('.section-header__options') || target.closest('.section-header__window-mode')) return
        onSelect?.(id)
        onSelectAllTasks?.(id)
      }}
    >
      {/* Collapse arrow — outside label so it sits further left */}
      <button
        className="section-header__arrow"
        onClick={() => onToggleCollapse(id)}
        type="button"
        disabled={archived}
      >
        <svg
          className={`section-header__arrow-icon${collapsed ? ' section-header__arrow-icon--collapsed' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M4.5 6L8 9.5L11.5 6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div className="section-header__label">
        {/* Title */}
        {isEditing ? (
          <div className="section-header__title-sizer" data-value={editValue || t('section.defaultName')}>
            <input
              ref={inputRef}
              className="section-header__title-input"
              value={editValue}
              placeholder={t('section.defaultName')}
              size={1}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onBlur={handleSubmit}
              spellCheck={false}
            />
          </div>
        ) : (
          <span
            className="section-header__title"
            /*
              **Doppelklick auf den Titel benennt um** (#236, Sven 03.10.2026). Der einfache Klick
              klappt deshalb erst nach kurzer Wartezeit ein — sonst klappte der Doppelklick zweimal.
              Der Pfeil klappt weiter sofort.
            */
            onClick={archived ? undefined : (e) => {
              // Schreibgeschützt (Wiederkehrend, Kurz notiert) gibt es kein Umbenennen — sofort klappen.
              if (readonly) { onToggleCollapse(id); return }
              if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current)
              collapseTimerRef.current = null
              if (e.detail >= 2) return
              collapseTimerRef.current = setTimeout(() => {
                collapseTimerRef.current = null
                onToggleCollapse(id)
              }, TITLE_CLICK_DELAY)
            }}
            onDoubleClick={archived || readonly ? undefined : (e) => {
              // Nicht bis zum Kopf: Dort wählt der Doppelklick alle Aufgaben.
              e.stopPropagation()
              if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current)
              collapseTimerRef.current = null
              setIsEditing(true)
            }}
          >
            {editValue}
          </span>
        )}

        {/* Task count — hidden for empty sections */}
        {taskCount > 0 && <span className="section-header__count">{taskCount}</span>}
        {archived && <span className="section-header__badge">{t('section.archivedBadge')}</span>}
        {dropTarget === 'end' && <span className="section-header__drop-end">{t('listToolbar.dropAtEnd')}</span>}
      </div>

      {/* Options (ellipsis) — hidden when readonly (e.g. virtual sections) and
          on the drag overlay clone (kept clean while dragging). */}
      {!readonly && !dragOverlay && (
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button className={`section-header__options${menuOpen ? ' section-header__options--open' : ''}`} type="button">
              <MoreHorizontal size={16} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            /*
              **Wohin der Fokus nach dem Menü geht, entscheidet der Kopf** (#236, #3). Radix gäbe ihn
              per `setTimeout` an ⋯ zurück. Startete „Umbenennen“ vorher (es lief per
              `requestAnimationFrame`), nahm die Rückgabe dem frischen Feld den Fokus, und `onBlur`
              speicherte unverändert — bei Abschnitten mit Aufgaben gewann sie das Rennen (gemessen
              im E2E, 03.10.2026). Und blieb der Fokus auf ⋯, öffneten Pfeil oder Enter das Menü
              wieder (#3). Darum: „Umbenennen“ startet erst hier; sonst zurück in die Liste — außer
              ein Klick daneben hat den Fokus schon woandershin gesetzt.
            */
            onCloseAutoFocus={(e) => {
              e.preventDefault()
              if (renameOnCloseRef.current) {
                renameOnCloseRef.current = false
                setIsEditing(true)
                return
              }
              const active = document.activeElement
              if (active && active !== document.body) return
              const list = rootRef.current?.parentElement?.closest<HTMLElement>('[tabindex]')
              list?.focus({ preventScroll: true })
            }}
          >
            {/*
              Wiederherstellen öffnet die Aufgaben nicht wieder (wie Todoist) — deshalb heißt der
              Eintrag „Nur Abschnitt …“, sobald es daneben den Weg mit Aufgaben gibt.
            */}
            {archived ? (
              onUnarchive && (reopenCount > 0 ? (
                <>
                  <DropdownMenuItem onSelect={() => onUnarchive(id, false)}>
                    {t('section.unarchiveSectionOnly')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => onUnarchive(id, true)}>
                    {t('section.unarchiveWithTasks', { count: reopenCount })}
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem onSelect={() => onUnarchive(id, false)}>
                  {t('section.unarchive')}
                </DropdownMenuItem>
              ))
            ) : (
              <>
                <DropdownMenuItem onSelect={() => { renameOnCloseRef.current = true }}>
                  {t('section.rename')}
                </DropdownMenuItem>
                {onArchive && (
                  <DropdownMenuItem onSelect={() => onArchive(id)}>
                    {t('section.archive')}
                  </DropdownMenuItem>
                )}
              </>
            )}
            <DropdownMenuItem
              className="context-menu-content__item--danger"
              onSelect={() => onDelete(id)}
            >
              {t('section.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Wave decoration — fills remaining width, clipped (not stretched). */}
      <DecorativeWave className="section-header__wave" />

      {/* Compact window-mode toggle, far right. Hidden at rest; revealed on the
          same hover that fades in the chevron (header or, in a project, its
          tasks — ProjectPage.scss). Not on virtual
          (readonly) sections, not on the drag overlay clone, and not when the
          project has a single section (the project-title toggle covers it). */}
      {!readonly && !dragOverlay && !archived && showWindowModeToggle && <WindowModeToggle iconOnly showShortcuts={false} sectionId={id} className="section-header__window-mode" />}
    </div>
  )
}
